import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { ChevronRight, ChevronLeft, FileText, ExternalLink, Search, Package, Flag, RefreshCw, Receipt, ListChecks, Plus, Pencil, X, Check, Sliders, ArrowLeftRight, Copy, Download, Trash2, MousePointerClick, Loader2 } from "lucide-react";
import { SellBitModal, TransferBitModal, BitDetailModal } from "@/components/StockCenterModals";
import bit3 from "@/components/icons/bits/bit-3blade-render.png";
import bit4 from "@/components/icons/bits/bit-4blade-render.png";
import bit5 from "@/components/icons/bits/bit-5blade-render.png";
import bit6 from "@/components/icons/bits/bit-6blade-render.png";
import bitTricone from "@/components/icons/bits/bit-tricone-render.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Warehouse registry (mirrors StockCenterModals) — used by the advanced-actions
// bulk transfer destination picker.
const WAREHOUSES = [
  { code: "UK", name: "United Kingdom" }, { code: "AUS", name: "Australia" },
  { code: "CH", name: "Switzerland" }, { code: "CA", name: "Canada" }, { code: "IN", name: "India" },
];

// Only treat a spec value as a clickable link if it's actually an http(s) URL.
// Some snapshot rows have a design-no or a bare number in the spec field; a bare
// value would otherwise resolve as a relative link (→ localhost/838) and 404. A
// non-URL value reads as "no spec sheet on file" instead of a broken link.
const specHref = (v) => { const s = (v || "").trim(); return /^https?:\/\//i.test(s) ? s : ""; };

// ── The "find a bit" funnel — the warehouse team's mental model: blade count
//    (or Tricone) → size → thread + quantity → spec PDF. A sibling view to the
//    full Table mode; both live under StockCenterPage's Browse/Table toggle.

// Each family has a real top-down PDC-bit illustration (flat clipart, unified
// red) so the blade count is obvious. Theme is the Rockpecker product MAROON
// across all families (not per-family colours) — calmer, on-brand. "Other" has
// no bit image → falls back to a simple glyph.
// Rockpecker products maroon — CSS var so it brightens in dark mode (defined in
// index.css: deep maroon in .light, bright maroon on dark for legibility).
const RP_MAROON = "hsl(var(--sc-maroon))";
const RP_MAROON_TINT = (a) => `hsl(var(--sc-maroon) / ${a})`;
// `scale` normalises apparent bit DIAMETER across renders — each PNG has the bit
// filling a different fraction of its canvas (measured: 3-blade 0.92 … 5-blade
// 0.68), so without this the discs look different sizes. scale = target / fill.
// Renders are square-cropped tight to the bit (≈96% fill each), so they render
// at equal apparent size with no per-family scale/nudge needed.
const FAMILY_META = {
  "3 Blade": { color: RP_MAROON, img: bit3 },
  "4 Blade": { color: RP_MAROON, img: bit4 },
  "5 Blade": { color: RP_MAROON, img: bit5 },
  "6 Blade": { color: RP_MAROON, img: bit6 },
  "Tricone": { color: RP_MAROON, img: bitTricone },
  "Other":   { color: RP_MAROON, icon: "M4 7h16v4H4zM4 13h16v4H4z" },
};
const famMeta = (f) => FAMILY_META[f] || FAMILY_META.Other;

// Display label only (data key stays "Tricone" so grouping/state are unaffected).
const FAMILY_LABEL = { "Tricone": "VRS / Tricone" };
const famLabel = (f) => FAMILY_LABEL[f] || f;

function fmtMoney(n, ccy) {
  if (n == null) return null;
  return `${ccy ? ccy + " " : ""}${Math.round(n).toLocaleString()}`;
}

// Typeset fractions elegantly: any "N/M" → a clean, COMPACT diagonal fraction
// (e.g. 3-1/2" → 3-¹⁄₂"). First we try the font's native `frac` feature (renders
// like Claude/Google when the font supports it); the small raised-numerator /
// lowered-denominator markup is the visual fallback so it's compact regardless
// of font support. Pure display; underlying string untouched.
function FmtFrac({ children }) {
  const s = String(children ?? "");
  if (!s) return null;
  const parts = s.split(/(\d+\/\d+)/);
  const dig = { fontSize: "0.64em", lineHeight: 1 };
  return (
    <>
      {parts.map((p, i) => {
        const m = p.match(/^(\d+)\/(\d+)$/);
        if (!m) return <React.Fragment key={i}>{p}</React.Fragment>;
        return (
          <span key={i} style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center" }}>
            <span style={{ ...dig, transform: "translateY(-0.30em)" }}>{m[1]}</span>
            <span style={{ fontSize: "0.95em", opacity: 0.5, margin: "0 -0.03em" }}>⁄</span>
            <span style={{ ...dig, transform: "translateY(0.16em)" }}>{m[2]}</span>
          </span>
        );
      })}
    </>
  );
}

// Numeric inches from a size label ('5-1/2"' → 5.5, '3-3/4"' → 3.75).
function sizeInches(s) {
  const raw = String(s || "").replace(/"/g, "").trim();
  let m = raw.match(/^(\d+)-(\d+)\/(\d+)$/);
  if (m) return +m[1] + +m[2] / +m[3];
  m = raw.match(/^(\d+)\/(\d+)$/);
  if (m) return +m[1] / +m[2];
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

// Size range across a family's in-stock sizes, e.g. 2" – 16". Sizes arrive
// sorted ascending; we take the first and last REAL sizes (skipping the "—"
// bucket and any implausible value — no drill bit is > 60", so a stray box
// dimension from older/dirty data can't blow the range out). "" if none.
function sizeRange(sizes) {
  const b = sizeBounds(sizes);
  if (!b) return "";
  return b.min === b.max ? b.min : `${b.min} – ${b.max}`;
}
// Min/max real sizes for a family (skips "—" and implausible >60" stray values).
// Returns {min, max} display strings, or null.
function sizeBounds(sizes) {
  const real = (sizes || [])
    .map((s) => s.size_in)
    .filter((s) => s && s !== "—")
    .filter((s) => { const n = sizeInches(s); return n != null && n > 0 && n <= 60; });
  if (!real.length) return null;
  return { min: real[0], max: real[real.length - 1] };
}

// Marking-number → year of manufacture (range table from Rockpecker; a bit's
// year is a pure function of its marking number). Returns null if out of range.
const MARKING_YEARS = [
  [5920, 6275, 2015], [6276, 6747, 2016], [6748, 7798, 2017], [7799, 8941, 2018],
  [8942, 10530, 2019], [10531, 14945, 2020], [14946, 17166, 2021], [17167, 20279, 2022],
  [20280, 23852, 2023], [23853, 26417, 2024], [26418, 28503, 2025], [28504, 29364, 2026],
];
function markingYear(marking) {
  const m = String(marking || "").match(/\d+/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  for (const [lo, hi, yr] of MARKING_YEARS) if (n >= lo && n <= hi) return yr;
  return null;
}

// Sort serials ascending by their numeric marking (then lexically as a tiebreak).
function sortSerials(serials) {
  return [...(serials || [])].sort((a, b) => {
    const na = parseInt(String(a).match(/\d+/)?.[0] ?? "", 10);
    const nb = parseInt(String(b).match(/\d+/)?.[0] ?? "", 10);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  });
}

// Human-readable common details shared by a product group (body type, nozzles,
// cutters). Returns an array of {label, value} for the ones present.
function commonDetails(d) {
  if (!d) return [];
  const out = [];
  const cut = (q, s) => [q, s].filter(Boolean).join("×");
  if (d.body_type) out.push({ label: "Body type", value: d.body_type });
  if (d.bit_size_mm) out.push({ label: "Size (mm)", value: `${d.bit_size_mm} mm` });
  const noz = cut(d.nozzle_count, d.nozzle_size);
  if (noz) out.push({ label: "Nozzles", value: noz });
  const g = cut(d.gauge_cutter_qty, d.gauge_cutter_size);
  if (g) out.push({ label: "Gauge cutters", value: g });
  const p = cut(d.pdc_qty, d.pdc_size);
  if (p) out.push({ label: "PDC cutters", value: p });
  const dome = cut(d.dome_qty, d.dome_size);
  if (dome) out.push({ label: "Dome", value: dome });
  return out;
}

// Renders the family's bit illustration (or a fallback glyph for "Other").
// Transparent round PNG renders; object-contain since they're not perfectly square.
function FamilySilhouette({ family, size = 40, opacity = 1 }) {
  const m = famMeta(family);
  if (m.img) {
    // Renders are square-cropped to the bit, so a plain square <img> renders them
    // all at equal apparent size — no scale factor needed.
    return <img src={m.img} alt="" className="sc-bit-img" style={{ opacity, display: "block", width: size, height: size, objectFit: "contain" }} draggable={false} aria-hidden="true" />;
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={m.color} style={{ opacity: 0.5 }} aria-hidden="true">
      <path d={m.icon} />
    </svg>
  );
}

// ── Level 3: Outlook-style split view — product detail left, spec PDF right ──
// ── Advanced actions lightbox ────────────────────────────────────────────────
// A power surface over a product's serials. Whole-row click selects (big target);
// keyboard: Space toggles, Shift+click/Shift+Space range-selects, ⌘/Ctrl+A all,
// Esc closes. The action bar RESHAPES by selection count: one bit → the full
// single-bit set (sell/transfer/edit/duplicate) + export/delete; many → bulk
// transfer / bulk-edit / export / delete, with the single-bit actions shown
// greyed AND captioned with WHY (so nothing is a dead-end mystery). Statuses
// come from a one-shot /bits fetch so we know which serials are sellable etc.
const SC_BULK_FIELDS = [
  ["status", "Status", ["in_stock", "unfinished", "in_transit", "overstock", "missing"]],
  ["shelf_location", "Shelf location", null],
  ["supplier", "Supplier", null],
];

function AdvancedActionsModal({ product, family, activeWh, canSell, canManage, onClose, onChanged, onOpenSell, onOpenTransfer, onOpenDetail }) {
  const serials = useMemo(() => sortSerials(product.serials || []), [product.serials]);
  const [bits, setBits] = useState({});          // marking -> bit doc (id, status…)
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(() => new Set());
  const [lastIdx, setLastIdx] = useState(null);   // anchor for shift-range
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState("status");
  const [bulkValue, setBulkValue] = useState("");
  const [xferOpen, setXferOpen] = useState(false);
  const [xferDest, setXferDest] = useState("");
  const rowRefs = React.useRef([]);

  // One fetch resolves every serial → full bit (so we know status/id). The funnel
  // only carries markings; actions need ids.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const wq = activeWh !== "ALL" ? `warehouse=${activeWh}&` : "";
        const res = await axios.get(`${API}/stock-center/bits?${wq}limit=2000`);
        const want = new Set(serials.map(String));
        const map = {};
        (res.data || []).forEach((b) => { if (want.has(String(b.marking_no))) map[String(b.marking_no)] = b; });
        if (live) setBits(map);
      } catch { /* row actions degrade; list still shows */ }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [serials, activeWh]);

  const selArr = useMemo(() => serials.filter((s) => sel.has(s)), [serials, sel]);
  const selBits = useMemo(() => selArr.map((s) => bits[s]).filter(Boolean), [selArr, bits]);
  const one = sel.size === 1 ? (bits[selArr[0]] || null) : null;
  const allOn = sel.size === serials.length && serials.length > 0;

  const setRange = (a, b) => {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    setSel((prev) => { const n = new Set(prev); for (let i = lo; i <= hi; i++) n.add(serials[i]); return n; });
  };
  const onRow = (idx, e) => {
    const s = serials[idx];
    if (e?.shiftKey && lastIdx != null) { setRange(lastIdx, idx); setLastIdx(idx); return; }
    setSel((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
    setLastIdx(idx);
  };
  const toggleAll = () => { setSel(allOn ? new Set() : new Set(serials)); setLastIdx(null); };

  // Keyboard: Esc close, ⌘/Ctrl+A select-all. Per-row Space handled on the row.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); }
      else if ((e.metaKey || e.ctrlKey) && (e.key === "a" || e.key === "A")) { e.preventDefault(); setSel(new Set(serials)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [serials, onClose]);

  const refresh = () => { onChanged?.(); };

  const doBulkEdit = async () => {
    const val = bulkValue.trim();
    if (!val) { toast.error("Enter a value"); return; }
    setBusy(true);
    try {
      const ids = selBits.map((b) => b.id);
      const res = await axios.post(`${API}/stock-center/bits/bulk-update`, { ids, changes: { [bulkField]: val } });
      toast.success(`Updated ${res.data?.updated ?? 0} bit(s)`);
      setBulkOpen(false); setBulkValue(""); setSel(new Set()); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
    finally { setBusy(false); }
  };

  const doBulkTransfer = async () => {
    if (!xferDest) { toast.error("Choose a destination"); return; }
    setBusy(true);
    let sent = 0, skipped = 0;
    for (const b of selBits) {
      if (b.status === "sold" || b.status === "in_transit") { skipped++; continue; }
      try {
        await axios.post(`${API}/stock-center/bits/${b.id}/transfer`, { to_warehouse: xferDest });
        sent++;
      } catch { skipped++; }                       // collision / scope → counted, not fatal
    }
    setBusy(false);
    const dn = (WAREHOUSES.find((w) => w.code === xferDest) || {}).name || xferDest;
    toast.success(`Sent ${sent} to ${dn}${skipped ? ` · ${skipped} skipped (sold, in transit, or marking exists there)` : ""}`);
    setXferOpen(false); setXferDest(""); setSel(new Set()); refresh();
  };

  const doBulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.size} selected bit(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const ids = selBits.map((b) => b.id);
      const res = await axios.post(`${API}/stock-center/bits/bulk-delete`, { ids });
      toast.success(`Deleted ${res.data?.deleted ?? 0} bit(s)`);
      setSel(new Set()); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
    finally { setBusy(false); }
  };

  const exportSel = () => {
    const rows = selBits.length ? selBits : Object.values(bits);
    if (!rows.length) { toast.message("Nothing to export"); return; }
    const cols = ["warehouse", "marking_no", "design_no", "status", "bit_size_in", "thread_size", "shelf_location", "supplier", "flags"];
    const cell = (v) => { const s = Array.isArray(v) ? v.join(" | ") : (v ?? ""); return /[",\n]/.test(String(s)) ? `"${String(s).replace(/"/g, '""')}"` : String(s); };
    const body = rows.map((b) => cols.map((c) => cell(b[c])).join(",")).join("\n");
    const blob = new Blob([cols.join(",") + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `bits_${famLabel(family)}_${product.size_in || ""}.csv`.replace(/\s+/g, "_"); a.click(); URL.revokeObjectURL(url);
  };

  const Pill = ({ icon: Icon, label, onClick, tone = "ghost", disabled, reason }) => {
    const base = "inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]";
    const tones = {
      sell: "border-0 text-white hover:brightness-110 active:brightness-95", // bg via style
      xfer: "border-0 text-white hover:brightness-110 active:brightness-95",
      ghost: "border border-[hsl(var(--border-strong,var(--border)))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50",
      danger: "border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[#A32D2D] dark:text-[#F09595] hover:bg-[#A32D2D]/8",
    };
    const style = disabled ? {} : tone === "sell" ? { backgroundColor: "#0F6E56" } : tone === "xfer" ? { backgroundColor: "#534AB7" } : {};
    return (
      <button type="button" onClick={disabled ? undefined : onClick} aria-disabled={disabled} title={disabled ? reason : label}
        className={`${base} ${disabled ? "border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 text-[hsl(var(--muted-foreground))]/50 cursor-not-allowed" : tones[tone]}`} style={style}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} role="dialog" aria-modal="true" aria-label="Advanced actions">
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="min-w-0">
            <div className="text-[14px] font-bold leading-tight inline-flex items-center gap-2"><Sliders className="w-4 h-4 text-[#534AB7]" /> {famLabel(family)} · <FmtFrac>{product.size_in}</FmtFrac></div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))]"><FmtFrac>{product.thread}</FmtFrac> · {serials.length} in stock</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50"><X className="w-4.5 h-4.5" /></button>
        </div>

        {/* select-all + hints */}
        <div className="flex items-center gap-2.5 px-4 py-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20">
          <button onClick={toggleAll} aria-label={allOn ? "Clear selection" : "Select all"}
            className={`w-[18px] h-[18px] rounded-[5px] border inline-flex items-center justify-center shrink-0 ${sel.size ? "bg-[#534AB7] border-[#534AB7] text-white" : "border-[hsl(var(--border-strong,var(--border)))]"}`}>
            {allOn ? <Check className="w-3 h-3" /> : sel.size ? <span className="w-2 h-[2px] bg-white rounded" /> : null}
          </button>
          <span className="text-[12px] text-[hsl(var(--muted-foreground))]"><span className="text-[hsl(var(--foreground))] font-medium">{sel.size}</span> of {serials.length} selected</span>
          <span className="ml-auto text-[10.5px] text-[hsl(var(--muted-foreground))]/70 hidden sm:inline">Space toggles · Shift+click range · ⌘A all · Esc close</span>
          {sel.size > 0 && <button onClick={() => { setSel(new Set()); setLastIdx(null); }} className="text-[11px] text-[#534AB7] font-medium">Clear</button>}
        </div>

        {/* serial rows */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-[12px] text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Loading serials…</div>
          ) : serials.length === 0 ? (
            <div className="py-10 text-center text-[12px] text-[hsl(var(--muted-foreground))]">No serials on hand.</div>
          ) : serials.map((s, idx) => {
            const on = sel.has(s);
            const b = bits[s];
            const st = b?.status || "";
            const stTone = st === "in_transit" ? "text-[#854F0B] bg-[#FAEEDA]" : st === "sold" ? "text-[#5F5E5A] bg-[hsl(var(--muted))]" : "text-[#04342C] bg-[#E1F5EE]";
            return (
              <div key={s} ref={(el) => (rowRefs.current[idx] = el)} role="checkbox" aria-checked={on} tabIndex={0}
                onClick={(e) => onRow(idx, e)}
                onKeyDown={(e) => { if (e.key === " ") { e.preventDefault(); onRow(idx, e); } }}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer border-b border-[hsl(var(--border))]/60 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#534AB7] ${on ? "bg-[#534AB7]/[0.06]" : "hover:bg-[hsl(var(--muted))]/30"}`}>
                <span className={`w-[18px] h-[18px] rounded-[5px] border inline-flex items-center justify-center shrink-0 ${on ? "bg-[#534AB7] border-[#534AB7] text-white" : "border-[hsl(var(--border-strong,var(--border)))]"}`}>
                  {on && <Check className="w-3 h-3" />}
                </span>
                <span className="font-mono-data text-[13px] text-[hsl(var(--foreground))] flex-1">{s}</span>
                <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))]">{markingYear(s) || "—"}</span>
                {st && <span className={`text-[10.5px] px-2 py-0.5 rounded ${stTone}`}>{st.replace("_", " ")}</span>}
              </div>
            );
          })}
        </div>

        {/* inline bulk-edit panel */}
        {bulkOpen && (
          <div className="px-4 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 flex flex-wrap items-end gap-2">
            <div><span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-1">Field</span>
              <select value={bulkField} onChange={(e) => { setBulkField(e.target.value); setBulkValue(""); }} className="h-9 px-2.5 text-[13px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                {SC_BULK_FIELDS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
              </select></div>
            <div className="flex-1 min-w-[140px]"><span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-1">New value</span>
              {(() => { const opts = (SC_BULK_FIELDS.find((f) => f[0] === bulkField) || [])[2];
                return opts ? (
                  <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="w-full h-9 px-2.5 text-[13px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                    <option value="">Choose…</option>{opts.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
                  </select>
                ) : (
                  <input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Type a value" className="w-full h-9 px-2.5 text-[13px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))]" />
                ); })()}
            </div>
            <button onClick={doBulkEdit} disabled={busy || !bulkValue.trim()} className="h-9 px-4 rounded-md text-[13px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: "#534AB7" }}>{busy ? "Applying…" : `Apply to ${sel.size}`}</button>
            <button onClick={() => setBulkOpen(false)} className="h-9 px-3 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Cancel</button>
          </div>
        )}
        {/* inline bulk-transfer panel */}
        {xferOpen && (
          <div className="px-4 py-3 border-t border-[hsl(var(--border))] bg-[#534AB7]/[0.05] flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[160px]"><span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] block mb-1">Send {sel.size} bit(s) to</span>
              <select value={xferDest} onChange={(e) => setXferDest(e.target.value)} className="w-full h-9 px-2.5 text-[13px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                <option value="">Choose destination…</option>
                {WAREHOUSES.filter((w) => w.code !== activeWh).map((w) => <option key={w.code} value={w.code}>{w.name}</option>)}
              </select></div>
            <button onClick={doBulkTransfer} disabled={busy || !xferDest} className="h-9 px-4 rounded-md text-[13px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: "#534AB7" }}>{busy ? "Sending…" : "Send"}</button>
            <button onClick={() => setXferOpen(false)} className="h-9 px-3 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Cancel</button>
          </div>
        )}

        {/* action bar — reshapes by selection count */}
        <div className="px-4 py-3 border-t border-[hsl(var(--border))]">
          {sel.size === 0 ? (
            <div className="flex flex-col items-center gap-1 py-1 text-center">
              <MousePointerClick className="w-5 h-5 text-[hsl(var(--muted-foreground))]/60" />
              <div className="text-[12.5px] text-[hsl(var(--muted-foreground))]">Select one or more serials to act on them</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]/70">One bit → sell, transfer, edit, duplicate · Many → transfer, bulk-edit, export, delete</div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {one ? (
                <>
                  {canSell && one.status !== "sold" && <Pill icon={Receipt} label="Mark sold" tone="sell" onClick={() => onOpenSell(one)} />}
                  {canManage && one.status !== "sold" && one.status !== "in_transit" && <Pill icon={ArrowLeftRight} label="Transfer" tone="xfer" onClick={() => onOpenTransfer(one)} />}
                  {canManage && <Pill icon={Pencil} label="Edit" onClick={() => onOpenDetail(one)} />}
                  {canManage && <Pill icon={Copy} label="Duplicate" onClick={async () => { try { await axios.post(`${API}/stock-center/bits/${one.id}/duplicate`); toast.success("Duplicated"); refresh(); } catch (e) { toast.error("Duplicate failed"); } }} />}
                </>
              ) : (
                <>
                  {canManage && <Pill icon={ArrowLeftRight} label={`Transfer all (${sel.size})`} tone="xfer" onClick={() => { setBulkOpen(false); setXferOpen(true); }} />}
                  {canManage && <Pill icon={Sliders} label="Change status / shelf / supplier" onClick={() => { setXferOpen(false); setBulkOpen(true); }} />}
                  {/* greyed single-bit actions WITH reasons so it's never a mystery */}
                  {canSell && <Pill icon={Receipt} label="Mark sold" disabled reason="Select exactly one bit to sell" />}
                  {canManage && <Pill icon={Pencil} label="Edit" disabled reason="Select exactly one bit to edit" />}
                  {canManage && <Pill icon={Copy} label="Duplicate" disabled reason="Select exactly one bit to duplicate" />}
                </>
              )}
              <Pill icon={Download} label="Export" onClick={exportSel} />
              {canManage && <span className="ml-auto"><Pill icon={Trash2} label={one ? "Delete" : `Delete all (${sel.size})`} tone="danger" onClick={doBulkDelete} /></span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SpecSplitView({ product, family, onBack, canViewCost, canSell, canManage, onSell, onBulkSell, onEditProduct, onAddAnother, onAdvanced }) {
  const m = famMeta(family);
  const url = specHref(product.spec_url);
  const serials = useMemo(() => sortSerials(product.serials || []), [product.serials]);
  const details = commonDetails(product.details);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch" data-testid="sc-split-view">
      {/* Left — product facts */}
      <div className="min-w-0">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <button onClick={onBack} className="group inline-flex items-center gap-2 text-[12px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="sc-back-to-sizes">
            <span className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-full rl-eye-btn text-[hsl(var(--foreground))]/70 group-hover:text-[hsl(var(--foreground))]"><ChevronLeft className="w-4 h-4" /></span>
            Back to sizes
          </button>
          {/* Action pills — wrap as WHOLE pills on a narrow screen (flex-wrap)
              and never let their labels break inside (whitespace-nowrap), so they
              drop to a second row instead of ballooning vertically on a phone. */}
          <div className="flex items-center gap-2 flex-wrap">
            {canManage && (
              <button onClick={onEditProduct} data-testid="sc-edit-product"
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-1.5 rounded-full rl-pill border border-[hsl(var(--border))] text-[hsl(var(--foreground))]/80 hover:bg-[hsl(var(--muted))]/60 hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--foreground))]/25 active:scale-[0.97] text-[10px] font-semibold uppercase tracking-[0.08em] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" title="Edit this product's shared details">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            {canManage && (
              <button onClick={onAddAnother} data-testid="sc-add-another"
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-1.5 rounded-full rl-pill border border-[#0F6E56]/40 bg-[#0F6E56]/5 text-[#0F6E56] dark:text-[#5DCAA5] hover:bg-[#0F6E56]/15 hover:border-[#0F6E56]/70 active:scale-[0.97] text-[10px] font-semibold uppercase tracking-[0.08em] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" title="Add another bit of this exact type">
                <Plus className="w-3.5 h-3.5" /> Add another
              </button>
            )}
            {canSell && (
              <button onClick={onBulkSell} data-testid="sc-mark-sold"
                className="rl-lit-btn inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.05em] text-white hover:brightness-110 active:brightness-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]"
                style={{ backgroundColor: "#0F6E56" }} title="Select multiple serials to mark sold">
                <ListChecks className="w-4 h-4" /> Mark multiple sold
              </button>
            )}
            {(canManage || canSell) && (
              <button onClick={onAdvanced} data-testid="sc-advanced-actions"
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-1.5 rounded-full rl-pill border border-[#534AB7]/40 bg-[#534AB7]/5 text-[#534AB7] dark:text-[#AFA9EC] hover:bg-[#534AB7]/15 hover:border-[#534AB7]/70 active:scale-[0.97] text-[10px] font-semibold uppercase tracking-[0.08em] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#534AB7]" title="Select serials and take advanced actions">
                <Sliders className="w-3.5 h-3.5" /> Advanced actions
              </button>
            )}
          </div>
        </div>
        <div className="rl-glass-edge rounded-xl bg-[hsl(var(--card))] p-4">
          <div className="flex items-center gap-2.5 mb-1">
            <span className="flex items-center justify-center w-11 h-11 rounded-lg" style={{ background: RP_MAROON_TINT(0.08) }}>
              <FamilySilhouette family={family} size={38} />
            </span>
            <div>
              <div className="text-[16px] font-bold leading-tight">{famLabel(family)} · <FmtFrac>{product.size_in}</FmtFrac></div>
              <div className="text-[12px] text-[hsl(var(--muted-foreground))]"><FmtFrac>{product.thread}</FmtFrac></div>
            </div>
          </div>
          {/* Stat row — every VALUE bottom-aligns to a shared baseline (min-h +
              items-end) so the big qty/value and the smaller design-no all sit on
              one line, and every LABEL lines up in the row beneath. Columns are
              PROPORTIONAL (not equal): unit value gets the widest track so the
              currency+amount never clips; design-no may truncate (secondary, full
              value is on the detail row below). */}
          <div className="grid grid-cols-[0.7fr_1.6fr_1fr] gap-3 my-3.5 py-3 border-y border-[hsl(var(--border))]">
            <div className="flex flex-col min-w-0">
              <div className="min-h-[26px] flex items-end font-mono-data font-extrabold tabular-nums text-[24px] leading-none" style={{ color: m.color }}>{product.qty}</div>
              <div className="text-[10px] uppercase tracking-[0.06em] text-[hsl(var(--muted-foreground))] mt-1.5">in stock</div>
            </div>
            {canViewCost && product.unit_value != null && (
              <div className="flex flex-col min-w-0">
                <div className="min-h-[26px] flex items-end font-mono-data font-bold tabular-nums text-[20px] leading-none text-[hsl(var(--foreground))] whitespace-nowrap">{fmtMoney(product.unit_value, product.currency)}</div>
                <div className="text-[10px] uppercase tracking-[0.06em] text-[hsl(var(--muted-foreground))] mt-1.5">unit value</div>
              </div>
            )}
            {product.design_no && (
              <div className="flex flex-col min-w-0">
                <div className="min-h-[26px] flex items-end font-mono-data text-[13px] leading-none text-[hsl(var(--muted-foreground))] truncate">{product.design_no}</div>
                <div className="text-[10px] uppercase tracking-[0.06em] text-[hsl(var(--muted-foreground))] mt-1.5">design no.</div>
              </div>
            )}
          </div>

          {/* Common details — specs shared by every bit in this group */}
          {details.length > 0 && (
            <div className="mb-3.5">
              <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/80 mb-2">Common details</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {details.map((d) => (
                  <div key={d.label} className="flex items-baseline justify-between gap-2 border-b border-[hsl(var(--border))]/50 pb-1">
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{d.label}</span>
                    <span className="font-mono-data text-[12px] text-[hsl(var(--foreground))]/85 text-right">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/80">Serials on hand <span className="font-mono-data">({serials.length})</span></span>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]/70">ascending</span>
          </div>
          {serials.length === 0 ? (
            <span className="text-[12px] text-[hsl(var(--muted-foreground))]">No serials recorded.</span>
          ) : (
            <div className="border border-[hsl(var(--border))] rounded-lg overflow-hidden max-h-[260px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 z-[1]">
                  <tr className="rl-thead">
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Serial</th>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Year</th>
                    {canSell && <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {serials.map((s) => {
                    const yr = markingYear(s);
                    return (
                      <tr key={s} className="border-t border-[hsl(var(--border))]/60 group/srow hover:bg-[hsl(var(--muted))]/30">
                        <td className="px-3 py-1.5 font-mono-data text-[hsl(var(--foreground))]">{s}</td>
                        <td className="px-3 py-1.5 font-mono-data text-[hsl(var(--muted-foreground))]">{yr || "—"}</td>
                        {canSell && (
                          <td className="px-3 py-1 text-right">
                            <button onClick={() => onSell(s)} data-testid={`sell-serial-${s}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.06em] px-3 py-1 rounded-full rl-pill border transition-all active:scale-95
                                bg-[hsl(var(--muted))]/50 border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]
                                hover:bg-[#0F6E56] hover:border-[#0F6E56] hover:text-white hover:shadow-[0_2px_6px_rgba(15,110,86,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]">
                              <Receipt className="w-3 h-3" /> Mark sold
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Right — spec sheet pane. The links are SharePoint documents that require
          an Office365 login and refuse to be iframed (frame-ancestors policy), so
          we present a rich preview card + a prominent "Open spec sheet" button
          that opens SharePoint in a new tab where the user is signed in. */}
      <div className="min-w-0">
        <div className="rl-glass-edge rounded-xl bg-[hsl(var(--card))] overflow-hidden flex flex-col h-full min-h-[420px]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
            <span className="text-[12px] font-semibold inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" /> Spec sheet</span>
          </div>
          <div className="flex-1 flex items-center justify-center p-6 bg-[hsl(var(--muted))]/30">
            {url ? (
              <div className="flex flex-col items-center text-center max-w-[320px]">
                {/* Stylised A4 document — not the real PDF (can't embed), a calm
                    representation so the pane reads as "a spec sheet lives here". */}
                <div className="relative w-[132px] h-[176px] mb-5">
                  <div className="absolute inset-0 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-md shadow-[0_8px_24px_rgba(40,30,20,0.12)] p-3.5 overflow-hidden">
                    <div className="h-2 rounded-sm mb-2.5" style={{ width: "62%", background: `${m.color}`, opacity: 0.75 }} />
                    <div className="h-1 rounded-sm bg-[hsl(var(--border))] mb-1.5" style={{ width: "92%" }} />
                    <div className="h-1 rounded-sm bg-[hsl(var(--border))] mb-1.5" style={{ width: "78%" }} />
                    <div className="h-[52px] rounded-sm my-2.5 flex items-center justify-center" style={{ background: RP_MAROON_TINT(0.08) }}>
                      <FamilySilhouette family={family} size={42} opacity={0.65} />
                    </div>
                    <div className="h-1 rounded-sm bg-[hsl(var(--border))] mb-1.5" style={{ width: "85%" }} />
                    <div className="h-1 rounded-sm bg-[hsl(var(--border))] mb-1.5" style={{ width: "55%" }} />
                    <div className="h-1 rounded-sm bg-[hsl(var(--border))]" style={{ width: "70%" }} />
                  </div>
                  <span className="absolute -bottom-2 -right-2 flex items-center justify-center w-9 h-9 rounded-lg bg-[#0F6E56] text-white text-[9px] font-bold shadow-md">PDF</span>
                </div>
                <div className="text-[14px] font-semibold text-[hsl(var(--foreground))]">{famLabel(family)} · <FmtFrac>{product.size_in}</FmtFrac> spec sheet</div>
                {product.design_no && <div className="font-mono-data text-[12px] text-[hsl(var(--muted-foreground))] mt-0.5">{product.design_no}</div>}
                <a href={url} target="_blank" rel="noopener noreferrer"
                  className="rl-lit-btn mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.05em] text-white hover:brightness-110 active:brightness-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]"
                  style={{ backgroundColor: "#0F6E56" }} data-testid="sc-open-spec">
                  Open spec sheet <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2.5">Opens in SharePoint · sign in with your Office 365 account</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-[hsl(var(--muted-foreground))] text-center">
                <FileText className="w-8 h-8 opacity-40" />
                <span className="text-[13px] font-medium text-[hsl(var(--foreground))]">No spec sheet linked</span>
                <span className="text-[12px] max-w-[260px]">This product doesn't have a SharePoint spec link yet. Add one to surface it here.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Multi-select bulk-sell: tick serials, fill one sale's details, and sell each
// selected bit (loops the per-bit /sell endpoint — one customer/order, many
// bits). Fetches each bit's id by marking within the active warehouse.
const SC_CCYS = ["USD", "EUR", "AUD", "CAD", "CHF", "INR", "GBP"];
function BulkSellModal({ product, family, activeWh, onClose, onDone }) {
  const allSerials = useMemo(() => sortSerials(product.serials || []), [product.serials]);
  const [picked, setPicked] = useState(() => new Set());
  const [clientNames, setClientNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    customer: "", price: product.unit_value ? String(product.unit_value) : "",
    currency: product.currency || "", sold_date: new Date().toISOString().slice(0, 10), order_no: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    axios.get(`${API}/clients`).then((r) => setClientNames((r.data || []).map((c) => c.name).filter(Boolean))).catch(() => {});
  }, []);
  const toggle = (s) => setPicked((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const allChecked = picked.size === allSerials.length && allSerials.length > 0;
  const toggleAll = () => setPicked(allChecked ? new Set() : new Set(allSerials));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer.trim()) { toast.error("Customer is required"); return; }
    if (picked.size === 0) { toast.error("Tick at least one serial to sell"); return; }
    setSaving(true);
    const wq = activeWh !== "ALL" ? `warehouse=${activeWh}&` : "";
    let ok = 0, fail = 0;
    for (const marking of allSerials.filter((s) => picked.has(s))) {
      try {
        const res = await axios.get(`${API}/stock-center/bits?${wq}q=${encodeURIComponent(marking)}`);
        const bit = (res.data || []).find((b) => String(b.marking_no) === String(marking) && b.status !== "sold");
        if (!bit) { fail++; continue; }
        await axios.post(`${API}/stock-center/bits/${bit.id}/sell`, {
          customer: f.customer.trim(), price: f.price, currency: f.currency,
          sold_date: f.sold_date, order_no: f.order_no, erp_destination: "none",
        });
        ok++;
      } catch (err) { fail++; }
    }
    setSaving(false);
    if (ok) toast.success(`Marked ${ok} bit${ok === 1 ? "" : "s"} sold${fail ? ` · ${fail} failed` : ""}`);
    else toast.error("No bits were sold");
    onDone?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl w-full max-w-lg max-h-[88vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="text-[15px] font-bold inline-flex items-center gap-2"><Receipt className="w-4 h-4 text-[#0F6E56]" /> Mark sold — select serials</div>
          <div className="text-[12px] text-[hsl(var(--muted-foreground))]">{famLabel(family)} · <FmtFrac>{product.size_in}</FmtFrac> · <FmtFrac>{product.thread}</FmtFrac></div>
        </div>
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          {/* serial picker table */}
          <div className="px-4 pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]/80">Serials <span className="font-mono-data">({picked.size}/{allSerials.length})</span></span>
              <button type="button" onClick={toggleAll} className="text-[11px] text-[#0F6E56] font-medium">{allChecked ? "Clear all" : "Select all"}</button>
            </div>
            <div className="border border-[hsl(var(--border))] rounded-lg overflow-hidden max-h-[34vh] overflow-y-auto">
              <table className="w-full text-[12px]">
                <tbody>
                  {allSerials.map((s) => {
                    const yr = markingYear(s);
                    const on = picked.has(s);
                    return (
                      <tr key={s} onClick={() => toggle(s)} className={`border-t border-[hsl(var(--border))]/60 first:border-t-0 cursor-pointer ${on ? "bg-[#0F6E56]/[0.07]" : "hover:bg-[hsl(var(--muted))]/30"}`}>
                        <td className="px-3 py-1.5 w-8"><input type="checkbox" checked={on} readOnly className="pointer-events-none" /></td>
                        <td className="px-2 py-1.5 font-mono-data text-[hsl(var(--foreground))]">{s}</td>
                        <td className="px-3 py-1.5 font-mono-data text-[hsl(var(--muted-foreground))] text-right">{yr || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* sale details */}
          <div className="px-4 py-3 space-y-2.5 border-t border-[hsl(var(--border))] mt-3">
            <div>
              <span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Customer *</span>
              <input value={f.customer} onChange={(e) => set("customer", e.target.value)} list="bulk-clients" autoComplete="off" autoFocus
                placeholder="Search clients or type a new buyer"
                className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" />
              <datalist id="bulk-clients">{clientNames.map((n) => <option key={n} value={n} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div><span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Price (each)</span>
                <input value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="Amount" className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] mt-0.5" /></div>
              <div><span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Currency</span>
                <select value={f.currency} onChange={(e) => set("currency", e.target.value)} className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] mt-0.5">
                  <option value="">—</option>{SC_CCYS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Sold date</span>
                <input type="date" value={f.sold_date} onChange={(e) => set("sold_date", e.target.value)} className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] mt-0.5" /></div>
              <div><span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Order no.</span>
                <input value={f.order_no} onChange={(e) => set("order_no", e.target.value)} className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] mt-0.5" /></div>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="text-[13px] px-3.5 py-2 rounded-md border border-[hsl(var(--border))]">Cancel</button>
            <button type="submit" disabled={saving || picked.size === 0} className="rl-lit-btn text-[13px] font-semibold px-4 py-2 rounded-md text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: "#0F6E56" }}>
              <Receipt className="w-4 h-4" /> {saving ? "Selling…" : `Confirm sale (${picked.size})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Quick "add another of this type": type one or more new serials; each becomes a
// new in-stock bit copying THIS product's specs (blades/size/thread/design/etc).
// Serial-only — the rest is inherited silently so it lands in the same product.
function AddAnotherModal({ product, family, activeWh, onClose, onDone }) {
  const [rows, setRows] = useState([""]);          // individual serial boxes
  const [bulkMode, setBulkMode] = useState(false); // paste-a-list mode
  const [bulkText, setBulkText] = useState("");
  const [saving, setSaving] = useState(false);

  const setRow = (i, v) => setRows((p) => p.map((x, j) => (j === i ? v : x)));
  const addRow = () => setRows((p) => [...p, ""]);
  const removeRow = (i) => setRows((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : p));

  // Final list = whichever mode is active.
  const list = (bulkMode ? bulkText.split(/[\s,;\n]+/) : rows)
    .map((s) => s.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (activeWh === "ALL") { toast.error("Pick a specific warehouse first to add stock"); return; }
    if (!list.length) { toast.error("Enter at least one serial / marking number"); return; }
    setSaving(true);
    const base = {
      warehouse: activeWh, status: "in_stock", blades: family.startsWith("Tricone") ? "" : family,
      bit_size_in: product.size_in === "—" ? "" : product.size_in, bit_size_mm: product.size_mm || "",
      thread_size: product.thread === "No thread" ? "" : product.thread, design_no: product.design_no || "",
      body_type: (product.details && product.details.body_type) || "", currency: product.currency || "",
      spec_sheet_url: product.spec_url || "", spec_sheet: product.spec_url || "",
      nozzle_count: (product.details || {}).nozzle_count || "", nozzle_size: (product.details || {}).nozzle_size || "",
      gauge_cutter_qty: (product.details || {}).gauge_cutter_qty || "", gauge_cutter_size: (product.details || {}).gauge_cutter_size || "",
      pdc_qty: (product.details || {}).pdc_qty || "", pdc_size: (product.details || {}).pdc_size || "",
    };
    try {
      await axios.post(`${API}/stock-center/bits/bulk`, list.map((mk) => ({ ...base, marking_no: mk })));
      toast.success(`Added ${list.length} bit${list.length === 1 ? "" : "s"} to ${activeWh}`);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl w-full max-w-md max-h-[88vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="text-[15px] font-bold inline-flex items-center gap-2"><Plus className="w-4 h-4 text-[#0F6E56]" /> Add another — {famLabel(family)} · {product.size_in}</div>
          <div className="text-[12px] text-[hsl(var(--muted-foreground))]">{product.thread} · copies this product's specs. Just add serial(s).</div>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          {activeWh === "ALL" && <div className="text-[12px] text-[#BA7517] bg-[#BA7517]/10 rounded-md px-3 py-2">Pick a specific warehouse tab first — stock is added per warehouse.</div>}

          {!bulkMode ? (
            <>
              <span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">New serial / marking number(s)</span>
              <div className="space-y-1.5">
                {rows.map((v, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input value={v} onChange={(e) => setRow(i, e.target.value)} autoFocus={i === 0}
                      placeholder="Marking no." data-testid={`add-serial-${i}`}
                      className="flex-1 h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] font-mono-data focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" />
                    <button type="button" onClick={() => removeRow(i)} disabled={rows.length === 1}
                      className="w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-md border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] hover:border-[hsl(var(--destructive))]/40 disabled:opacity-30 disabled:hover:text-[hsl(var(--muted-foreground))] disabled:hover:border-[hsl(var(--border))]"
                      aria-label="Remove">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addRow}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-[#0F6E56] dark:text-[#5DCAA5] hover:underline" data-testid="add-serial-row">
                <Plus className="w-3.5 h-3.5" /> Add another box
              </button>
            </>
          ) : (
            <div>
              <span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Paste a list of marking numbers</span>
              <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} autoFocus rows={5}
                placeholder={"e.g.\n28311\n28312\n28313\n…or 28311, 28312, 28313"}
                className="w-full px-2.5 py-2 rounded-md rl-carved bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] text-[13px] mt-0.5 font-mono-data focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" />
            </div>
          )}

          {/* Mode toggle */}
          <button type="button" onClick={() => setBulkMode((b) => !b)}
            className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline-offset-2 hover:underline" data-testid="add-bulk-toggle">
            {bulkMode ? "← Back to individual boxes" : "Or paste many at once →"}
          </button>

          <div className="flex items-center justify-between gap-2 pt-1 border-t border-[hsl(var(--border))]/60 mt-1">
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{list.length ? `${list.length} bit${list.length === 1 ? "" : "s"} → ${activeWh}` : ""}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="text-[13px] px-3.5 py-2 rounded-md border border-[hsl(var(--border))]">Cancel</button>
              <button type="submit" disabled={saving || !list.length} className="rl-lit-btn text-[13px] font-semibold px-4 py-2 rounded-md text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: "#0F6E56" }}>
                <Plus className="w-4 h-4" /> {saving ? "Adding…" : `Add ${list.length || ""}`.trim()}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit the product's SHARED specs and apply to every bit in the group (loops the
// per-bit PATCH). Per-serial edits are still available from the table rows.
function EditProductModal({ product, family, activeWh, onClose, onDone }) {
  const d = product.details || {};
  const [f, setF] = useState({
    thread_size: product.thread === "No thread" ? "" : product.thread,
    design_no: product.design_no || "", body_type: d.body_type || "",
    spec_sheet_url: product.spec_url || "",
    nozzle_count: d.nozzle_count || "", nozzle_size: d.nozzle_size || "",
    gauge_cutter_qty: d.gauge_cutter_qty || "", gauge_cutter_size: d.gauge_cutter_size || "",
    pdc_qty: d.pdc_qty || "", pdc_size: d.pdc_size || "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Resolve the bits in this group by marking, then PATCH each with the
      // edited shared fields. (No spec fields in bulk-update, so loop PATCH.)
      const wq = activeWh !== "ALL" ? `warehouse=${activeWh}&` : "";
      const seen = new Set();
      let ok = 0;
      for (const marking of (product.serials || [])) {
        if (seen.has(marking)) continue; seen.add(marking);
        try {
          const res = await axios.get(`${API}/stock-center/bits?${wq}q=${encodeURIComponent(marking)}`);
          const bit = (res.data || []).find((b) => String(b.marking_no) === String(marking));
          if (!bit) continue;
          await axios.patch(`${API}/stock-center/bits/${bit.id}`, {
            ...f, spec_sheet: f.spec_sheet_url,
          });
          ok++;
        } catch (e2) { /* skip */ }
      }
      toast.success(`Updated ${ok} bit${ok === 1 ? "" : "s"} in this product`);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const F = ({ k, label, ph }) => (
    <div><span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">{label}</span>
      <input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph}
        className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[13px] mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" /></div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl w-full max-w-lg max-h-[88vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-[hsl(var(--border))]">
          <div className="text-[15px] font-bold inline-flex items-center gap-2"><Pencil className="w-4 h-4 text-[#0F6E56]" /> Edit product — {famLabel(family)} · {product.size_in}</div>
          <div className="text-[12px] text-[hsl(var(--muted-foreground))]">Applies to all {product.qty} bit{product.qty === 1 ? "" : "s"} in this product. Edit a single serial from its row instead.</div>
        </div>
        <form onSubmit={submit} className="p-4 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <F k="thread_size" label="Thread" ph='e.g. 2-3/8" API REG PIN' />
            <F k="design_no" label="Design no." ph="Design code" />
            <F k="body_type" label="Body type" ph="PDC drill bit…" />
            <div className="col-span-2"><span className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Spec sheet link (SharePoint)</span>
              <input value={f.spec_sheet_url} onChange={(e) => set("spec_sheet_url", e.target.value)} placeholder="https://…sharepoint.com/…"
                className="w-full h-9 px-2.5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] mt-0.5 font-mono-data focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]" /></div>
            <F k="nozzle_count" label="Nozzle count" ph="" />
            <F k="nozzle_size" label="Nozzle size" ph="" />
            <F k="gauge_cutter_qty" label="Gauge cutter qty" ph="" />
            <F k="gauge_cutter_size" label="Gauge cutter size" ph="" />
            <F k="pdc_qty" label="PDC qty" ph="" />
            <F k="pdc_size" label="PDC size" ph="" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-[13px] px-3.5 py-2 rounded-md border border-[hsl(var(--border))]">Cancel</button>
            <button type="submit" disabled={saving} className="rl-lit-btn text-[13px] font-semibold px-4 py-2 rounded-md text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: "#0F6E56" }}>
              <Check className="w-4 h-4" /> {saving ? "Saving…" : "Apply to all"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StockCenterBrowse({ activeWh, canViewCost, canSell, canManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [famSel, setFamSel] = useState(null);   // selected family (L2)
  const [prodSel, setProdSel] = useState(null); // {size, product} (L3)
  const [sellBit, setSellBit] = useState(null); // full bit doc being sold (single)
  const [bulkSell, setBulkSell] = useState(false); // multi-select sell modal open
  const [addAnother, setAddAnother] = useState(false); // quick add-another modal
  const [editProduct, setEditProduct] = useState(false); // group-edit modal
  const [advanced, setAdvanced] = useState(false); // advanced-actions lightbox
  const [xferBit, setXferBit] = useState(null);    // single bit being transferred
  const [detailBit, setDetailBit] = useState(null); // single bit detail drawer

  const fetchProducts = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/stock-center/products${activeWh !== "ALL" ? `?warehouse=${activeWh}` : ""}`);
      setData(res.data || { families: [] });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load products");
    } finally { setLoading(false); }
  }, [activeWh]);

  useEffect(() => { fetchProducts(); setFamSel(null); setProdSel(null); }, [fetchProducts]);

  // Selling is per physical bit; the funnel only knows markings, so look the bit
  // up by marking (within the active warehouse) before opening the sell modal.
  const startSell = useCallback(async (marking) => {
    if (!marking) { toast.error("No serial to sell in this group"); return; }
    try {
      const wq = activeWh !== "ALL" ? `warehouse=${activeWh}&` : "";
      const res = await axios.get(`${API}/stock-center/bits?${wq}q=${encodeURIComponent(marking)}`);
      const bit = (res.data || []).find((b) => String(b.marking_no) === String(marking) && b.status !== "sold")
        || (res.data || []).find((b) => String(b.marking_no) === String(marking));
      if (!bit) { toast.error(`Couldn't find bit ${marking}`); return; }
      setSellBit(bit);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open the sale"); }
  }, [activeWh]);

  const families = data?.families || [];
  const curFamily = useMemo(() => families.find((f) => f.family === famSel) || null, [families, famSel]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rl-glass-edge rounded-2xl overflow-hidden animate-pulse">
            <div className="h-20 bg-[hsl(var(--muted))]/50" />
            <div className="p-3.5 space-y-2"><div className="h-4 w-20 bg-[hsl(var(--muted))]/50 rounded" /><div className="h-6 w-14 bg-[hsl(var(--muted))]/50 rounded" /></div>
          </div>
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <Package className="w-9 h-9 text-[hsl(var(--muted-foreground))]/50" />
        <p className="text-[14px] text-[hsl(var(--foreground))]">{error}</p>
        <button onClick={fetchProducts} className="rl-lit-btn inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.05em] px-4 py-2 rounded-full text-white hover:brightness-110 active:brightness-95 transition-all" style={{ backgroundColor: "#0F6E56" }}><RefreshCw className="w-4 h-4" /> Retry</button>
      </div>
    );
  }

  // ── Level 3 ──
  if (curFamily && prodSel) {
    return (
      <>
        <SpecSplitView product={prodSel.product} family={curFamily.family} canViewCost={canViewCost}
          canSell={canSell} canManage={canManage} onSell={startSell} onBulkSell={() => setBulkSell(true)}
          onAddAnother={() => setAddAnother(true)} onEditProduct={() => setEditProduct(true)}
          onAdvanced={() => setAdvanced(true)} onBack={() => setProdSel(null)} />
        {advanced && (
          <AdvancedActionsModal product={prodSel.product} family={curFamily.family} activeWh={activeWh}
            canSell={canSell} canManage={canManage}
            onClose={() => setAdvanced(false)}
            onChanged={fetchProducts}
            onOpenSell={(b) => setSellBit(b)}
            onOpenTransfer={(b) => setXferBit(b)}
            onOpenDetail={(b) => setDetailBit(b)} />
        )}
        {xferBit && (
          <TransferBitModal bit={xferBit} onClose={() => setXferBit(null)}
            onDone={() => { setXferBit(null); fetchProducts(); }} />
        )}
        {detailBit && (
          <BitDetailModal bit={detailBit} canManage={canManage} canViewCost={canViewCost}
            onClose={() => setDetailBit(null)}
            onSaved={() => { fetchProducts(); }} />
        )}
        {sellBit && (
          <SellBitModal bit={sellBit} onClose={() => setSellBit(null)}
            onDone={() => { setSellBit(null); fetchProducts(); }} />
        )}
        {bulkSell && (
          <BulkSellModal product={prodSel.product} family={curFamily.family} activeWh={activeWh}
            onClose={() => setBulkSell(false)}
            onDone={() => { setBulkSell(false); fetchProducts(); }} />
        )}
        {addAnother && (
          <AddAnotherModal product={prodSel.product} family={curFamily.family} activeWh={activeWh}
            onClose={() => setAddAnother(false)}
            onDone={() => { setAddAnother(false); fetchProducts(); }} />
        )}
        {editProduct && (
          <EditProductModal product={prodSel.product} family={curFamily.family} activeWh={activeWh}
            onClose={() => setEditProduct(false)}
            onDone={() => { setEditProduct(false); fetchProducts(); }} />
        )}
      </>
    );
  }

  // ── Level 2: size shelf within a family ──
  if (curFamily) {
    const m = famMeta(curFamily.family);
    return (
      <div data-testid="sc-size-shelf">
        {/* items-start so the back disc aligns to the TOP of the (often two-line)
            title block instead of floating centered against it. */}
        <div className="flex items-start gap-3 mb-4">
          <button onClick={() => setFamSel(null)} className="group inline-flex items-center gap-2 text-[12px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors shrink-0" data-testid="sc-back-to-types">
            <span className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-full rl-eye-btn text-[hsl(var(--foreground))]/70 group-hover:text-[hsl(var(--foreground))]"><ChevronLeft className="w-4 h-4" /></span>
            <span className="hidden sm:inline">Back to bit types</span>
          </button>
          <div className="min-w-0">
            <div className="text-[16px] font-bold flex items-center gap-2"><FamilySilhouette family={curFamily.family} size={26} /> <span className="min-w-0">{famLabel(curFamily.family)} — choose a size</span></div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Inch primary · mm shown alongside · {curFamily.qty} in stock across {curFamily.size_count} sizes</div>
          </div>
        </div>
        {/* ── DESKTOP rows (sm+) — the ORIGINAL open layout, restored verbatim:
            each thread is its own full row (size column with "↳" for extra
            threads), chip, design, value, and qty + "in stock" label. ── */}
        <div className="hidden sm:block rl-glass-edge rounded-xl overflow-hidden bg-[hsl(var(--card))]">
          {curFamily.sizes.map((sz, i) => (
            <div key={sz.size_in + i} className={i < curFamily.sizes.length - 1 ? "border-b border-[hsl(var(--border))]" : ""}>
              {sz.threads.map((p, j) => (
                <button key={j} onClick={() => setProdSel({ size: sz.size_in, product: p })}
                  className="w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-[hsl(var(--muted))]/30 transition-colors group"
                  data-testid="sc-product-row">
                  <div className="min-w-[96px]">
                    {j === 0 ? (
                      <>
                        <span className="font-mono-data font-extrabold text-[18px] text-[hsl(var(--foreground))]"><FmtFrac>{sz.size_in}</FmtFrac></span>
                        {sz.size_mm && <div className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))]">{sz.size_mm} mm</div>}
                      </>
                    ) : <span className="font-mono-data text-[12px] text-[hsl(var(--muted-foreground))]/50">↳</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] text-[#185FA5] dark:text-[#85B7EB] bg-[#185FA5]/10 border border-[#185FA5]/30 rounded-full px-2.5 py-0.5 inline-flex items-center gap-1.5">
                      <FmtFrac>{p.thread}</FmtFrac>
                      {p.needs_review && <Flag className="w-2.5 h-2.5 text-[#BA7517]" />}
                    </span>
                    {p.design_no && <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))] ml-2">{p.design_no}</span>}
                  </div>
                  {canViewCost && p.unit_value != null && (
                    <span className="font-mono-data text-[12px] text-[hsl(var(--muted-foreground))] hidden md:inline">{fmtMoney(p.unit_value, p.currency)}</span>
                  )}
                  <div className="text-right min-w-[64px]">
                    <span className="font-mono-data font-extrabold text-[20px]" style={{ color: m.color }}>{p.qty}</span>
                    <div className="text-[9px] uppercase tracking-[0.06em] text-[hsl(var(--muted-foreground))]">in stock</div>
                  </div>
                  {specHref(p.spec_url)
                    ? <FileText className="w-4 h-4 text-[hsl(var(--muted-foreground))]/50 group-hover:text-[#0F6E56]" title="Has spec sheet" />
                    : <span className="w-4" />}
                  <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]/40" />
                </button>
              ))}
            </div>
          ))}
          {curFamily.sizes.length === 0 && <div className="px-4 py-10 text-center text-[13px] text-[hsl(var(--muted-foreground))]">No sizes in stock.</div>}
        </div>

        {/* ── MOBILE rows (<sm) — compact: the size label renders ONCE at the top
            of each block, then its threads stack as tall, easy-to-tap rows (each
            ≥52px) beneath, separated by a dashed rule. Single line per thread:
            chip · design · qty · spec · chevron. Qty number stands alone (no
            label — the size header gives context). Built to fit ~360px with no
            wrapping. ── */}
        <div className="sm:hidden rl-glass-edge rounded-xl overflow-hidden bg-[hsl(var(--card))]">
          {curFamily.sizes.map((sz, i) => (
            <div key={sz.size_in + i} className={`px-3.5 py-2.5 ${i < curFamily.sizes.length - 1 ? "border-b border-[hsl(var(--border))]" : ""}`}>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-mono-data font-extrabold text-[17px] text-[hsl(var(--foreground))]"><FmtFrac>{sz.size_in}</FmtFrac></span>
                {sz.size_mm && <span className="font-mono-data text-[11px] text-[hsl(var(--muted-foreground))]">{sz.size_mm} mm</span>}
              </div>
              <div className="divide-y divide-dashed divide-[hsl(var(--border))]/70">
                {sz.threads.map((p, j) => (
                  <button key={j} onClick={() => setProdSel({ size: sz.size_in, product: p })}
                    className="w-full text-left flex items-center gap-2 min-h-[48px] py-2 px-2 -mx-2 rounded-lg active:bg-[hsl(var(--muted))]/50 transition-colors"
                    data-testid="sc-product-row">
                    <span className="text-[12px] text-[#185FA5] dark:text-[#85B7EB] bg-[#185FA5]/10 border border-[#185FA5]/30 rounded-full px-2.5 py-0.5 inline-flex items-center gap-1.5 whitespace-nowrap shrink-0">
                      <FmtFrac>{p.thread}</FmtFrac>
                      {p.needs_review && <Flag className="w-2.5 h-2.5 text-[#BA7517] shrink-0" />}
                    </span>
                    {p.design_no && <span className="font-mono-data text-[10px] text-[hsl(var(--muted-foreground))] truncate min-w-0">{p.design_no}</span>}
                    <span className="font-mono-data font-extrabold text-[18px] ml-auto shrink-0" style={{ color: m.color }}>{p.qty}</span>
                    {specHref(p.spec_url)
                      ? <FileText className="w-4 h-4 text-[hsl(var(--muted-foreground))]/50 shrink-0" title="Has spec sheet" />
                      : <span className="inline-block w-4 shrink-0" />}
                    <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]/40 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ))}
          {curFamily.sizes.length === 0 && <div className="px-4 py-10 text-center text-[13px] text-[hsl(var(--muted-foreground))]">No sizes in stock.</div>}
        </div>
      </div>
    );
  }

  // ── Level 1: bit-type cards ──
  if (families.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-[hsl(var(--muted-foreground))]">
        <Package className="w-9 h-9 opacity-40" />
        <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">No stock to browse here yet</span>
        <span className="text-[12px]">Bits will appear grouped by type once stock is added.</span>
      </div>
    );
  }
  return (
    <div data-testid="sc-family-cards">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]/70 mb-3 ml-0.5">Pick a bit type</div>
      {/* Bit-as-hero cards: the illustration sits large + faded in the corner as
          product identity; stats read clean on top. 4-across, maroon theme. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5">
        {families.map((f) => {
          const m = famMeta(f.family);
          return (
            <button key={f.family} onClick={() => setFamSel(f.family)} data-testid={`sc-family-${f.family.replace(/\s+/g, "-")}`}
              className="group/card relative text-left rl-glass-edge rounded-2xl overflow-hidden bg-[hsl(var(--card))] min-h-[148px] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_22px_rgba(40,30,20,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]">
              {/* hero bit render — large, semi-faded in the bottom-right corner,
                  unfades + lifts slightly on hover (transparent round PNG). Per-
                  family nudge aligns differently-framed renders into one corner.
                  MOBILE: scaled down + fainter (the card is much narrower there,
                  so the full-size render swamped the text); full size on sm+. */}
              <div className="pointer-events-none absolute -right-3 -bottom-4 w-[120px] h-[120px] origin-bottom-right scale-[0.74] opacity-[0.28] sm:scale-100 sm:opacity-[0.45] group-hover/card:opacity-100 group-hover/card:scale-[0.78] sm:group-hover/card:scale-[1.04] transition-all duration-200 select-none" aria-hidden="true">
                <FamilySilhouette family={f.family} size={120} />
              </div>
              {/* MOBILE readability scrim — a soft card-colour wash from the left so
                  the stats always sit on a legible base over the render. sm:hidden
                  (desktop cards are wide enough not to need it). z between image
                  (z-0) and content (z-1). */}
              <div className="sm:hidden pointer-events-none absolute inset-0 z-0" aria-hidden="true"
                   style={{ background: "linear-gradient(108deg, hsl(var(--card)) 46%, hsl(var(--card) / 0.55) 66%, transparent 88%)" }} />
              {f.family === "Tricone" && (
                <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded-full z-10" style={{ color: RP_MAROON, background: RP_MAROON_TINT(0.14) }}>cones</span>
              )}
              <div className="relative z-[1] p-4">
                <div className="text-[15px] font-bold">{famLabel(f.family)}</div>
                <div className="flex items-baseline gap-1.5 mt-5">
                  <span className="font-mono-data font-extrabold text-[30px] leading-none" style={{ color: m.color, letterSpacing: "-0.02em" }}>{f.qty.toLocaleString()}</span>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">in stock</span>
                </div>
                {/* Size range — min/max joined by a short gradient track, with the
                    distinct-size count inline ("· N sizes"). No grey labels: the
                    word "sizes" self-explains the count. */}
                <div className="flex items-center gap-2.5 mt-3.5 flex-wrap">
                  {(() => {
                    const b = sizeBounds(f.sizes);
                    if (!b) return <span className="font-mono-data text-[13px] text-[hsl(var(--muted-foreground))]">No sizes</span>;
                    return (
                      <span className="flex items-center gap-2">
                        <span className="font-mono-data text-[13px] font-semibold text-[hsl(var(--foreground))]/85"><FmtFrac>{b.min}</FmtFrac></span>
                        <span className="h-[3px] w-8 rounded-full shrink-0" style={{ background: `linear-gradient(90deg, ${RP_MAROON_TINT(0.35)}, ${RP_MAROON})` }} aria-hidden="true" />
                        <span className="font-mono-data text-[13px] font-semibold text-[hsl(var(--foreground))]/85"><FmtFrac>{b.max}</FmtFrac></span>
                      </span>
                    );
                  })()}
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">· {f.size_count} size{f.size_count === 1 ? "" : "s"}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
